import { useEffect, useState } from "react";
import {
  fetchBooks,
  createBook,
  deleteBook,
  updateBook,
  resolveCover,
  fetchCategoriesManage,
  createCategory,
  updateCategory,
  deleteCategory,
  fetchAllReviews,
  updateReview,
  deleteReview,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Trash2,
  Upload,
  Plus,
  Loader2,
  Link as LinkIcon,
  Pencil,
  X,
  LogOut,
  ImageIcon,
  Save,
  Check as CheckIcon,
  Star,
  MessageSquare,
} from "lucide-react";

const initialForm = {
  title: "",
  description: "",
  price: "",
  original_price: "",
  buy_link: "",
  tags: "",
  category: "",
};

export const Admin = () => {
  const { user, logout } = useAuth();
  const [books, setBooks] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingBook, setEditingBook] = useState(null);

  const loadBooks = () => fetchBooks().then(setBooks);
  const loadCategories = () => fetchCategoriesManage().then(setCategoryList);

  const load = () => {
    setLoading(true);
    Promise.all([loadBooks(), loadCategories()]).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price || !coverFile) {
      toast.error("Title, price and cover image are required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("price", form.price);
      if (form.original_price) fd.append("original_price", form.original_price);
      fd.append("buy_link", form.buy_link);
      fd.append("tags", form.tags);
      fd.append("category", form.category || "General");
      fd.append("cover_image", coverFile);
      await createBook(fd);
      toast.success(`"${form.title}" added to the shelf.`);
      setForm({ ...initialForm });
      setCoverFile(null);
      loadBooks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add book");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}" from the website entirely?`)) return;
    try {
      await deleteBook(id);
      toast.success("Removed");
      if (editingBook?.id === id) setEditingBook(null);
      loadBooks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <main
      data-testid="admin-page"
      className="min-h-screen bg-[var(--bw-admin-bg)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between border-b border-[var(--bw-primary)]/30 pb-6 gap-4 flex-wrap">
          <div>
            <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
              — bookwrck admin
            </p>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">
              Manage the shelf
            </h1>
            {user && (
              <p
                data-testid="admin-username-label"
                className="mt-2 font-mono-plex text-xs text-[var(--bw-secondary)]"
              >
                signed in as {user.username}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <p className="font-mono-plex text-xs md:text-sm text-[var(--bw-secondary)]">
              {books.length} titles
            </p>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 border border-[var(--bw-primary)]/30 rounded-full px-3 py-1.5 text-sm hover:bg-[var(--bw-primary)] hover:text-[var(--bw-bg)] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 mt-10">
          {/* Add form */}
          <form
            data-testid="admin-book-form"
            onSubmit={submit}
            className="lg:col-span-5 bg-white border border-[var(--bw-primary)]/20 p-6 md:p-8 h-fit lg:sticky lg:top-24"
          >
            <h2 className="font-serif-display text-3xl mb-6 flex items-center gap-3">
              <Plus className="w-5 h-5 text-[var(--bw-accent)]" />
              Add a new book
            </h2>

            <div className="space-y-4">
              <Field
                label="Book title"
                testId="admin-title"
                value={form.title}
                onChange={setField("title")}
              />

              <div>
                <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                  Category
                </label>
                <select
                  data-testid="admin-category-select"
                  value={form.category}
                  onChange={setField("category")}
                  className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
                >
                  <option value="">— pick a category —</option>
                  {categoryList.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {categoryList.length === 0 && (
                  <p className="mt-1 text-[11px] text-[var(--bw-secondary)]">
                    Add a category first in the section below.
                  </p>
                )}
              </div>

              <div>
                <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                  Cover image
                </label>
                <label
                  htmlFor="admin-cover-input"
                  className="mt-1 flex items-center gap-3 border border-dashed border-[var(--bw-primary)]/30 p-4 cursor-pointer hover:border-[var(--bw-accent)] transition-colors"
                >
                  <Upload className="w-4 h-4 text-[var(--bw-accent)]" />
                  <span className="text-sm text-[var(--bw-secondary)] truncate">
                    {coverFile ? coverFile.name : "Click to upload a cover (JPG/PNG)"}
                  </span>
                </label>
                <input
                  id="admin-cover-input"
                  data-testid="admin-cover-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                />
                {coverFile && (
                  <div className="mt-3 border border-[var(--bw-border)] w-24 aspect-[3/4] overflow-hidden">
                    <img
                      src={URL.createObjectURL(coverFile)}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                  Description
                </label>
                <textarea
                  data-testid="admin-description"
                  value={form.description}
                  onChange={setField("description")}
                  rows={5}
                  className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Sale price ($) *"
                  type="number"
                  step="0.01"
                  testId="admin-price"
                  value={form.price}
                  onChange={setField("price")}
                />
                <Field
                  label="Original price ($)"
                  type="number"
                  step="0.01"
                  testId="admin-original-price"
                  value={form.original_price}
                  onChange={setField("original_price")}
                  placeholder="optional"
                />
              </div>
              <p className="-mt-2 text-[11px] text-[var(--bw-secondary)]">
                If you fill both, the original price shows as a strikethrough
                on the public page.
              </p>

              <div>
                <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)] flex items-center gap-1.5">
                  <LinkIcon className="w-3 h-3" />
                  Lemon Squeezy buy link
                </label>
                <input
                  data-testid="admin-buy-link"
                  value={form.buy_link}
                  onChange={setField("buy_link")}
                  placeholder="https://yourstore.lemonsqueezy.com/checkout/…"
                  className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm font-mono-plex"
                />
              </div>
            </div>

            <button
              data-testid="admin-submit-button"
              type="submit"
              disabled={submitting}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] py-4 hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding…
                </>
              ) : (
                <>Add to shelf</>
              )}
            </button>
          </form>

          {/* Table */}
          <div className="lg:col-span-7">
            <div className="border border-[var(--bw-primary)]/20 bg-white overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[var(--bw-primary)]/20 font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                <div className="col-span-7">Book</div>
                <div className="col-span-3">Price</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {loading ? (
                <div className="py-16 text-center text-sm text-[var(--bw-secondary)]">
                  Loading…
                </div>
              ) : books.length === 0 ? (
                <div className="py-16 text-center text-sm text-[var(--bw-secondary)]">
                  No books yet — add the first one.
                </div>
              ) : (
                books.map((b) => (
                  <AdminRow
                    key={b.id}
                    book={b}
                    onDelete={remove}
                    onEdit={() => setEditingBook(b)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Categories manager */}
        <CategoryManager
          categoryList={categoryList}
          onChange={loadCategories}
        />

        {/* Reviews manager */}
        <ReviewManager books={books} />
      </div>

      {editingBook && (
        <EditBookDialog
          book={editingBook}
          categoryList={categoryList}
          onClose={() => setEditingBook(null)}
          onSaved={() => {
            setEditingBook(null);
            loadBooks();
          }}
        />
      )}
    </main>
  );
};

const Field = ({ label, testId, value, onChange, type = "text", ...rest }) => (
  <div>
    <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
      {...rest}
    />
  </div>
);

const AdminRow = ({ book, onDelete, onEdit }) => (
  <div
    data-testid={`admin-row-${book.id}`}
    className="grid grid-cols-12 gap-2 px-4 py-4 border-b border-[var(--bw-primary)]/10 items-center hover:bg-[var(--bw-admin-bg)]"
  >
    <div className="col-span-7 flex items-center gap-3 min-w-0">
      <img
        src={resolveCover(book.cover_url)}
        alt=""
        className="w-10 h-14 object-cover border border-[var(--bw-border)]"
      />
      <div className="min-w-0">
        <p className="font-serif-display text-lg leading-tight truncate">
          {book.title}
        </p>
        <p className="text-xs text-[var(--bw-secondary)] truncate">
          {book.category || "no category"}
        </p>
      </div>
    </div>
    <div className="col-span-3 font-mono-plex text-sm">
      ${book.price?.toFixed(2)}
      {book.original_price && book.original_price > book.price && (
        <span className="ml-2 line-through text-[var(--bw-secondary)] text-xs">
          ${book.original_price.toFixed(2)}
        </span>
      )}
    </div>
    <div className="col-span-2 text-right flex items-center justify-end gap-1">
      <button
        data-testid={`admin-edit-${book.id}`}
        onClick={onEdit}
        className="p-2 text-[var(--bw-secondary)] hover:text-[var(--bw-primary)] transition-colors"
        aria-label={`Edit ${book.title}`}
      >
        <Pencil className="w-4 h-4" />
      </button>
      <button
        data-testid={`admin-delete-${book.id}`}
        onClick={() => onDelete(book.id, book.title)}
        className="p-2 hover:text-[var(--bw-accent)] transition-colors"
        aria-label={`Delete ${book.title}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const EditBookDialog = ({ book, categoryList = [], onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: book.title || "",
    description: book.description || "",
    price: book.price ?? "",
    original_price: book.original_price ?? "",
    buy_link: book.buy_link || "",
    tags: (book.tags || []).join(", "),
    category: book.category || "",
  });
  const [newCover, setNewCover] = useState(null);
  const [saving, setSaving] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("price", form.price);
      if (form.original_price === "" || form.original_price === null) {
        fd.append("clear_original_price", "true");
      } else {
        fd.append("original_price", form.original_price);
      }
      fd.append("buy_link", form.buy_link);
      fd.append("tags", form.tags);
      fd.append("category", form.category || "General");
      if (newCover) fd.append("cover_image", newCover);
      await updateBook(book.id, fd);
      toast.success("Book updated");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-edit-dialog"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white border border-[var(--bw-primary)]/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bw-border)]">
          <h2 className="font-serif-display text-2xl">Edit book</h2>
          <button
            data-testid="admin-edit-close"
            onClick={onClose}
            className="p-2 hover:text-[var(--bw-accent)]"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <Field
            label="Book title"
            testId="admin-edit-title"
            value={form.title}
            onChange={setField("title")}
          />
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Category
            </label>
            <select
              data-testid="admin-edit-category-select"
              value={form.category}
              onChange={setField("category")}
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            >
              <option value="">— pick a category —</option>
              {categoryList.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
              {form.category &&
                !categoryList.find((c) => c.name === form.category) && (
                  <option value={form.category}>{form.category} (legacy)</option>
                )}
            </select>
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Description
            </label>
            <textarea
              data-testid="admin-edit-description"
              value={form.description}
              onChange={setField("description")}
              rows={5}
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Sale price ($)"
              type="number"
              step="0.01"
              testId="admin-edit-price"
              value={form.price}
              onChange={setField("price")}
            />
            <Field
              label="Original price ($)"
              type="number"
              step="0.01"
              testId="admin-edit-original-price"
              value={form.original_price}
              onChange={setField("original_price")}
              placeholder="blank = no discount"
            />
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Lemon Squeezy buy link
            </label>
            <input
              data-testid="admin-edit-buy-link"
              value={form.buy_link}
              onChange={setField("buy_link")}
              className="mt-1 w-full border border-[var(--bw-primary)]/20 bg-white p-3 outline-none focus:border-[var(--bw-accent)] text-sm font-mono-plex"
            />
          </div>
          <div>
            <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
              Cover image
            </label>
            <div className="mt-2 flex items-center gap-4">
              <img
                src={
                  newCover
                    ? URL.createObjectURL(newCover)
                    : resolveCover(book.cover_url)
                }
                alt=""
                className="w-16 h-24 object-cover border border-[var(--bw-border)]"
              />
              <label
                htmlFor="admin-edit-cover-input"
                className="inline-flex items-center gap-2 border border-dashed border-[var(--bw-primary)]/30 px-4 py-2 cursor-pointer hover:border-[var(--bw-accent)] transition-colors text-sm"
              >
                <ImageIcon className="w-4 h-4" />
                {newCover ? newCover.name : "Replace image"}
              </label>
              <input
                id="admin-edit-cover-input"
                data-testid="admin-edit-cover-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setNewCover(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--bw-border)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm hover:text-[var(--bw-accent)]"
          >
            Cancel
          </button>
          <button
            data-testid="admin-edit-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] px-6 py-2.5 text-sm hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin;

const CategoryManager = ({ categoryList, onChange }) => {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await createCategory(newName.trim());
      toast.success(`Added "${newName.trim()}"`);
      setNewName("");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.name);
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try {
      await updateCategory(id, editName.trim());
      toast.success("Renamed");
      setEditingId(null);
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await deleteCategory(c.id);
      toast.success("Deleted");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section
      data-testid="category-manager"
      className="mt-16 border border-[var(--bw-primary)]/20 bg-white p-6 md:p-8"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
            — categories
          </p>
          <h2 className="font-serif-display text-3xl md:text-4xl leading-none mt-2">
            Manage categories
          </h2>
          <p className="text-sm text-[var(--bw-secondary)] mt-2 max-w-md">
            These are the pill filters shown on the homepage. Every book must
            pick one from this list.
          </p>
        </div>
        <form onSubmit={add} className="flex items-center gap-2">
          <input
            data-testid="category-manager-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name"
            className="border border-[var(--bw-primary)]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--bw-accent)]"
          />
          <button
            data-testid="category-manager-add-button"
            type="submit"
            disabled={adding}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] px-4 py-2 text-sm hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
          >
            {adding ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            Add
          </button>
        </form>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {categoryList.length === 0 && (
          <p className="text-sm text-[var(--bw-secondary)] italic">
            No categories yet — add your first one.
          </p>
        )}
        {categoryList.map((c) => (
          <div
            key={c.id}
            data-testid={`category-chip-${c.name}`}
            className="group inline-flex items-center gap-1.5 rounded-full border border-[var(--bw-border)] pl-3 pr-1.5 py-1 text-sm bg-[var(--bw-bg)]"
          >
            {editingId === c.id ? (
              <>
                <input
                  data-testid={`category-edit-input-${c.id}`}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit(c.id);
                    if (e.key === "Escape") setEditingId(null);
                  }}
                  autoFocus
                  className="bg-transparent outline-none text-sm min-w-[110px]"
                />
                <button
                  data-testid={`category-save-${c.id}`}
                  onClick={() => saveEdit(c.id)}
                  className="p-1 text-[var(--bw-accent)] hover:opacity-70"
                  aria-label="Save"
                >
                  <CheckIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="p-1 hover:opacity-70"
                  aria-label="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <span>{c.name}</span>
                <button
                  data-testid={`category-edit-${c.id}`}
                  onClick={() => startEdit(c)}
                  className="p-1 text-[var(--bw-secondary)] hover:text-[var(--bw-primary)]"
                  aria-label={`Rename ${c.name}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <button
                  data-testid={`category-delete-${c.id}`}
                  onClick={() => remove(c)}
                  className="p-1 text-[var(--bw-secondary)] hover:text-[var(--bw-accent)]"
                  aria-label={`Delete ${c.name}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const StarRow = ({ value = 0, onChange }) => {
  const interactive = typeof onChange === "function";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => {
        const active = s <= value;
        const Tag = interactive ? "button" : "span";
        return (
          <Tag
            key={s}
            type={interactive ? "button" : undefined}
            onClick={interactive ? () => onChange(s) : undefined}
            className={interactive ? "cursor-pointer" : ""}
            aria-label={interactive ? `Rate ${s}` : undefined}
          >
            <Star
              className={`w-4 h-4 ${
                active
                  ? "fill-[var(--bw-accent)] text-[var(--bw-accent)]"
                  : "text-[var(--bw-border)]"
              }`}
            />
          </Tag>
        );
      })}
    </div>
  );
};

const ReviewManager = ({ books }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ reviewer_name: "", rating: 5, comment: "" });
  const [saving, setSaving] = useState(false);
  const [filterBookId, setFilterBookId] = useState("all");

  const load = () => {
    setLoading(true);
    fetchAllReviews()
      .then(setReviews)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const bookMap = books.reduce((acc, b) => {
    acc[b.id] = b;
    return acc;
  }, {});

  const visible =
    filterBookId === "all"
      ? reviews
      : reviews.filter((r) => r.book_id === filterBookId);

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({
      reviewer_name: r.reviewer_name,
      rating: r.rating,
      comment: r.comment,
    });
  };

  const save = async (id) => {
    setSaving(true);
    try {
      await updateReview(id, draft);
      toast.success("Review updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    const bookTitle = bookMap[r.book_id]?.title || "this book";
    if (!window.confirm(`Delete ${r.reviewer_name}'s review of "${bookTitle}"?`))
      return;
    try {
      await deleteReview(r.id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section
      data-testid="review-manager"
      className="mt-16 border border-[var(--bw-primary)]/20 bg-white p-6 md:p-8"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono-plex text-xs uppercase tracking-[0.35em] text-[var(--bw-secondary)]">
            — reviews
          </p>
          <h2 className="font-serif-display text-3xl md:text-4xl leading-none mt-2 flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-[var(--bw-accent)]" />
            Manage reviews
          </h2>
          <p className="text-sm text-[var(--bw-secondary)] mt-2 max-w-md">
            Every review posted on any book, in one place. Edit or delete
            anything that shouldn&apos;t be there.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
            Filter
          </label>
          <select
            data-testid="review-filter-select"
            value={filterBookId}
            onChange={(e) => setFilterBookId(e.target.value)}
            className="border border-[var(--bw-primary)]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--bw-accent)]"
          >
            <option value="all">All books ({reviews.length})</option>
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="py-10 text-center text-sm text-[var(--bw-secondary)]">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Loading reviews…
          </div>
        ) : visible.length === 0 ? (
          <div
            data-testid="review-manager-empty"
            className="py-10 text-center border border-dashed border-[var(--bw-border)]"
          >
            <p className="text-sm text-[var(--bw-secondary)]">
              No reviews {filterBookId === "all" ? "yet" : "for this book yet"}.
            </p>
          </div>
        ) : (
          visible.map((r) => {
            const book = bookMap[r.book_id];
            const isEditing = editingId === r.id;
            return (
              <article
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="grid md:grid-cols-12 gap-4 border border-[var(--bw-border)] p-4"
              >
                <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                  {book ? (
                    <>
                      <img
                        src={resolveCover(book.cover_url)}
                        alt=""
                        className="w-10 h-14 object-cover border border-[var(--bw-border)] shrink-0"
                      />
                      <div className="min-w-0">
                        <p
                          data-testid={`admin-review-book-${r.id}`}
                          className="font-serif-display text-sm leading-tight truncate"
                        >
                          {book.title}
                        </p>
                        <p className="text-[11px] text-[var(--bw-secondary)] truncate">
                          {book.category}
                        </p>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-[var(--bw-secondary)] italic">
                      book removed
                    </p>
                  )}
                </div>

                <div className="md:col-span-7 min-w-0">
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <input
                          data-testid={`admin-review-name-input-${r.id}`}
                          value={draft.reviewer_name}
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              reviewer_name: e.target.value,
                            }))
                          }
                          className="border border-[var(--bw-primary)]/20 px-2 py-1 text-sm outline-none focus:border-[var(--bw-accent)] font-serif-display"
                        />
                        <StarRow
                          value={draft.rating}
                          onChange={(v) => setDraft((d) => ({ ...d, rating: v }))}
                        />
                      </div>
                      <textarea
                        data-testid={`admin-review-comment-input-${r.id}`}
                        value={draft.comment}
                        onChange={(e) =>
                          setDraft((d) => ({ ...d, comment: e.target.value }))
                        }
                        rows={3}
                        className="w-full border border-[var(--bw-primary)]/20 p-2 text-sm outline-none focus:border-[var(--bw-accent)]"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-serif-display text-base leading-none">
                          {r.reviewer_name}
                        </p>
                        <StarRow value={r.rating} />
                        <span className="font-mono-plex text-[10px] uppercase tracking-widest text-[var(--bw-secondary)]">
                          {new Date(r.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed whitespace-pre-line">
                        {r.comment}
                      </p>
                    </>
                  )}
                </div>

                <div className="md:col-span-2 flex items-start justify-end gap-1">
                  {isEditing ? (
                    <>
                      <button
                        data-testid={`admin-review-save-${r.id}`}
                        onClick={() => save(r.id)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 rounded-full bg-[var(--bw-primary)] text-[var(--bw-bg)] px-3 py-1.5 text-xs hover:bg-[var(--bw-accent)] transition-colors disabled:opacity-70"
                      >
                        {saving ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="p-1.5 text-[var(--bw-secondary)] hover:text-[var(--bw-primary)]"
                        aria-label="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid={`admin-review-edit-${r.id}`}
                        onClick={() => startEdit(r)}
                        className="p-2 text-[var(--bw-secondary)] hover:text-[var(--bw-primary)] transition-colors"
                        aria-label="Edit review"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-review-delete-${r.id}`}
                        onClick={() => remove(r)}
                        className="p-2 hover:text-[var(--bw-accent)] transition-colors"
                        aria-label="Delete review"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
};

